/**
 * ContentForge — Calendar View
 *
 * Month + Week views showing articles by publish/create date.
 * Color-coded by status, with article preview on click.
 */

import { html, nothing, type TemplateResult } from "lit";
import type { GatewayBrowserClient } from "../gateway.ts";

// ─── Types ──────────────────────────────────────────────────────────────

export type CfArticleSummary = {
  id: string;
  keywordId: string;
  siteId: string;
  title: string;
  status: "draft" | "scheduled" | "published" | "failed";
  articleType: string;
  wordCount: number;
  rankMathScore: number | null;
  productCount: number;
  internalLinkCount: number;
  faqCount: number;
  wpUrl: string | null;
  wpPostId: number | null;
  featuredImagePath: string | null;
  totalCostUsd: number;
  costBreakdown: Record<string, number>;
  publishedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

export type CfCalendarProps = {
  client: GatewayBrowserClient | null;
  activeSiteId: string | null;
  onRefresh: () => void;
};

// ─── State ──────────────────────────────────────────────────────────────

let _articles: CfArticleSummary[] = [];
let _loading = false;
let _viewMode: "month" | "week" = "month";
let _currentDate = new Date();
let _selectedArticle: CfArticleSummary | null = null;
let _rerenderFn: (() => void) | null = null;

// ─── Constants ──────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  published: "#34d399",
  scheduled: "#2dd4bf",
  draft: "#60a5fa",
  failed: "#f87171",
};

const STATUS_LABELS: Record<string, string> = {
  published: "Published",
  scheduled: "Scheduled",
  draft: "Draft",
  failed: "Failed",
};

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// ─── Data Loading ───────────────────────────────────────────────────────

async function loadArticles(client: GatewayBrowserClient | null, siteId: string | null) {
  if (!client) {
    return;
  }
  _loading = true;
  _rerenderFn?.();

  try {
    const res = await client.request<{ articles: CfArticleSummary[] }>(
      "contentforge.articles.list",
      { siteId: siteId ?? undefined },
    );
    _articles = res.articles ?? [];
  } catch (err) {
    console.error("[Calendar] Failed to load articles:", err);
    _articles = [];
  }

  _loading = false;
  _rerenderFn?.();
}

// ─── Helpers ────────────────────────────────────────────────────────────

function getArticleDate(a: CfArticleSummary): Date {
  const ts = a.publishedAt ?? a.createdAt;
  return new Date(ts);
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getMonthDays(year: number, month: number): Date[] {
  const days: Date[] = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Pad start to Monday
  let startDow = firstDay.getDay();
  if (startDow === 0) {
    startDow = 7;
  } // Sunday = 7
  for (let i = startDow - 1; i > 0; i--) {
    days.push(new Date(year, month, 1 - i));
  }

  // Month days
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, month, d));
  }

  // Pad end to fill grid (6 rows = 42 cells)
  while (days.length < 42) {
    days.push(new Date(year, month + 1, days.length - lastDay.getDate() - (startDow - 1) + 1));
  }

  return days;
}

function getWeekDays(date: Date): Date[] {
  const days: Date[] = [];
  const d = new Date(date);
  const dow = d.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + mondayOffset);
  for (let i = 0; i < 7; i++) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function rankMathBadge(score: number | null): TemplateResult {
  if (score === null) {
    return html`
      <span class="cf-cal__badge cf-cal__badge--muted">—</span>
    `;
  }
  const color = score >= 80 ? "#34d399" : score >= 50 ? "#fbbf24" : "#f87171";
  return html`<span class="cf-cal__badge" style="background: ${color}; color: #000;">${score}</span>`;
}

function formatCost(usd: number): string {
  return usd > 0 ? `$${usd.toFixed(2)}` : "$0.00";
}

// ─── Render ─────────────────────────────────────────────────────────────

export function renderCfCalendar(props: CfCalendarProps, rerender: () => void) {
  _rerenderFn = rerender;

  // Load on first render or when site changes
  if (_articles.length === 0 && !_loading) {
    void loadArticles(props.client, props.activeSiteId);
  }

  const today = new Date();

  return html`
    <div class="cf-calendar">
      <!-- Header -->
      <div class="cf-calendar__header">
        <div class="cf-calendar__nav">
          <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${() => {
            navigateMonth(-1);
            rerender();
          }}>←</button>
          <h2 class="cf-calendar__title">
            ${MONTH_NAMES[_currentDate.getMonth()]} ${_currentDate.getFullYear()}
          </h2>
          <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${() => {
            navigateMonth(1);
            rerender();
          }}>→</button>
        </div>
        <div class="cf-calendar__controls">
          <button
            class="cf-btn cf-btn--sm ${_viewMode === "month" ? "cf-btn--primary" : "cf-btn--ghost"}"
            @click=${() => {
              _viewMode = "month";
              rerender();
            }}
          >Month</button>
          <button
            class="cf-btn cf-btn--sm ${_viewMode === "week" ? "cf-btn--primary" : "cf-btn--ghost"}"
            @click=${() => {
              _viewMode = "week";
              rerender();
            }}
          >Week</button>
          <button
            class="cf-btn cf-btn--sm cf-btn--ghost"
            @click=${() => {
              _currentDate = new Date();
              rerender();
            }}
          >Today</button>
          <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${() => loadArticles(props.client, props.activeSiteId)}>
            Refresh
          </button>
        </div>
      </div>

      <!-- Legend -->
      <div class="cf-calendar__legend">
        ${Object.entries(STATUS_COLORS).map(
          ([status, color]) => html`
          <span class="cf-calendar__legend-item">
            <span class="cf-calendar__legend-dot" style="background: ${color}"></span>
            ${STATUS_LABELS[status]}
          </span>
        `,
        )}
        <span class="cf-calendar__legend-item" style="margin-left: auto; opacity: 0.6;">
          ${_articles.length} articles total
        </span>
      </div>

      <!-- Loading -->
      ${
        _loading
          ? html`
              <div class="cf-loading">Loading articles…</div>
            `
          : nothing
      }

      <!-- Grid -->
      ${!_loading && _viewMode === "month" ? renderMonthView(today) : nothing}
      ${!_loading && _viewMode === "week" ? renderWeekView(today) : nothing}

      <!-- Article Preview Modal -->
      ${_selectedArticle ? renderArticlePreview(_selectedArticle, rerender) : nothing}
    </div>
  `;
}

function navigateMonth(delta: number) {
  _currentDate = new Date(_currentDate.getFullYear(), _currentDate.getMonth() + delta, 1);
}

// ─── Month View ─────────────────────────────────────────────────────────

function renderMonthView(today: Date) {
  const year = _currentDate.getFullYear();
  const month = _currentDate.getMonth();
  const days = getMonthDays(year, month);

  return html`
    <div class="cf-calendar__grid">
      <!-- Day headers -->
      ${DAY_NAMES.map((d) => html`<div class="cf-calendar__day-header">${d}</div>`)}

      <!-- Day cells -->
      ${days.map((day) => {
        const isCurrentMonth = day.getMonth() === month;
        const isToday = sameDay(day, today);
        const dayArticles = _articles.filter((a) => sameDay(getArticleDate(a), day));

        return html`
          <div class="cf-calendar__cell ${isCurrentMonth ? "" : "cf-calendar__cell--muted"} ${isToday ? "cf-calendar__cell--today" : ""}">
            <span class="cf-calendar__cell-day">${day.getDate()}</span>
            <div class="cf-calendar__cell-articles">
              ${dayArticles.slice(0, 3).map(
                (a) => html`
                <div
                  class="cf-calendar__article-dot"
                  style="background: ${STATUS_COLORS[a.status] ?? "#94a3b8"}"
                  title="${a.title} (${a.status})"
                  @click=${() => {
                    _selectedArticle = a;
                    _rerenderFn?.();
                  }}
                ></div>
              `,
              )}
              ${
                dayArticles.length > 3
                  ? html`
                <span class="cf-calendar__more">+${dayArticles.length - 3}</span>
              `
                  : nothing
              }
            </div>
            ${
              dayArticles.length > 0
                ? html`
              <div class="cf-calendar__cell-titles">
                ${dayArticles.slice(0, 2).map(
                  (a) => html`
                  <div
                    class="cf-calendar__cell-title"
                    style="border-left: 2px solid ${STATUS_COLORS[a.status] ?? "#94a3b8"}"
                    @click=${() => {
                      _selectedArticle = a;
                      _rerenderFn?.();
                    }}
                    title="${a.title}"
                  >${a.title.slice(0, 25)}${a.title.length > 25 ? "…" : ""}</div>
                `,
                )}
              </div>
            `
                : nothing
            }
          </div>
        `;
      })}
    </div>
  `;
}

// ─── Week View ──────────────────────────────────────────────────────────

function renderWeekView(today: Date) {
  const days = getWeekDays(_currentDate);

  return html`
    <div class="cf-calendar__week">
      ${days.map((day) => {
        const isToday = sameDay(day, today);
        const dayArticles = _articles.filter((a) => sameDay(getArticleDate(a), day));

        return html`
          <div class="cf-calendar__week-day ${isToday ? "cf-calendar__week-day--today" : ""}">
            <div class="cf-calendar__week-header">
              <span class="cf-calendar__week-name">${DAY_NAMES[day.getDay() === 0 ? 6 : day.getDay() - 1]}</span>
              <span class="cf-calendar__week-date">${day.getDate()} ${MONTH_NAMES[day.getMonth()].slice(0, 3)}</span>
            </div>
            <div class="cf-calendar__week-articles">
              ${
                dayArticles.length === 0
                  ? html`
                      <div class="cf-calendar__week-empty">No articles</div>
                    `
                  : dayArticles.map(
                      (a) => html`
                  <div
                    class="cf-calendar__week-card"
                    style="border-left: 3px solid ${STATUS_COLORS[a.status] ?? "#94a3b8"}"
                    @click=${() => {
                      _selectedArticle = a;
                      _rerenderFn?.();
                    }}
                  >
                    <div class="cf-calendar__week-card-title">${a.title}</div>
                    <div class="cf-calendar__week-card-meta">
                      <span>${a.wordCount} words</span>
                      <span>${rankMathBadge(a.rankMathScore)}</span>
                      <span>${formatCost(a.totalCostUsd)}</span>
                    </div>
                  </div>
                `,
                    )
              }
            </div>
          </div>
        `;
      })}
    </div>
  `;
}

// ─── Article Preview Modal ──────────────────────────────────────────────

function renderArticlePreview(article: CfArticleSummary, rerender: () => void) {
  const statusColor = STATUS_COLORS[article.status] ?? "#94a3b8";
  const publishDate = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : article.status === "published"
      ? "Published ✓"
      : article.status === "scheduled"
        ? "Scheduled ⏳"
        : "Not published";
  const createdDate = new Date(article.createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return html`
    <div class="cf-modal-overlay" @click=${(e: Event) => {
      if ((e.target as HTMLElement).classList.contains("cf-modal-overlay")) {
        _selectedArticle = null;
        rerender();
      }
    }}>
      <div class="cf-modal cf-modal--article">
        <!-- Close Button -->
        <button class="cf-modal__close" @click=${() => {
          _selectedArticle = null;
          rerender();
        }}>✕</button>

        <!-- Header -->
        <div class="cf-modal__header">
          <span class="cf-modal__status" style="background: ${statusColor}">${STATUS_LABELS[article.status] ?? article.status}</span>
          <h2 class="cf-modal__title">${article.title}</h2>
        </div>

        <!-- Stats Grid -->
        <div class="cf-modal__stats">
          <div class="cf-modal__stat">
            <span class="cf-modal__stat-value">${rankMathBadge(article.rankMathScore)}</span>
            <span class="cf-modal__stat-label">Rank Math</span>
          </div>
          <div class="cf-modal__stat">
            <span class="cf-modal__stat-value">${article.wordCount.toLocaleString()}</span>
            <span class="cf-modal__stat-label">Words</span>
          </div>
          <div class="cf-modal__stat">
            <span class="cf-modal__stat-value">${article.productCount}</span>
            <span class="cf-modal__stat-label">Products</span>
          </div>
          <div class="cf-modal__stat">
            <span class="cf-modal__stat-value">${article.internalLinkCount}</span>
            <span class="cf-modal__stat-label">Internal Links</span>
          </div>
          <div class="cf-modal__stat">
            <span class="cf-modal__stat-value">${article.faqCount}</span>
            <span class="cf-modal__stat-label">FAQs</span>
          </div>
          <div class="cf-modal__stat">
            <span class="cf-modal__stat-value">${formatCost(article.totalCostUsd)}</span>
            <span class="cf-modal__stat-label">Total Cost</span>
          </div>
        </div>

        <!-- Details -->
        <div class="cf-modal__details">
          <div class="cf-modal__detail-row">
            <span class="cf-modal__detail-label">Created</span>
            <span class="cf-modal__detail-value">${createdDate}</span>
          </div>
          <div class="cf-modal__detail-row">
            <span class="cf-modal__detail-label">Published</span>
            <span class="cf-modal__detail-value">${publishDate}</span>
          </div>
          <div class="cf-modal__detail-row">
            <span class="cf-modal__detail-label">Type</span>
            <span class="cf-modal__detail-value">${article.articleType}</span>
          </div>
          ${
            article.wpUrl
              ? html`
            <div class="cf-modal__detail-row">
              <span class="cf-modal__detail-label">WP URL</span>
              <a class="cf-modal__detail-link" href="${article.wpUrl}" target="_blank" rel="noopener">${article.wpUrl}</a>
            </div>
          `
              : nothing
          }
          ${
            Object.keys(article.costBreakdown).length > 0
              ? html`
            <div class="cf-modal__detail-row">
              <span class="cf-modal__detail-label">Cost Breakdown</span>
              <span class="cf-modal__detail-value">
                ${Object.entries(article.costBreakdown).map(
                  ([agent, cost]) =>
                    html`<span class="cf-modal__cost-chip">${agent}: $${cost.toFixed(4)}</span> `,
                )}
              </span>
            </div>
          `
              : nothing
          }
        </div>
      </div>
    </div>
  `;
}
